import styled from '@emotion/styled';
import Icons from '@shared/components/core/images/Icons';
import { BodySmall, LabelMedium } from '@shared/components/core/text/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Collapsible guidance for authoring skills the on-device assistant can use reliably. Written for
 * non-technical users — plain language, concrete do's, no jargon. Collapsed by default so the editor
 * (and the "Teach a skill" review) stays uncluttered; one click reveals the full list. Mirrors
 * docs/ai-skills-user-guide.md.
 */
export default function SkillBestPractices() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const tips: { key: string; fallback: string }[] = [
        {
            key: 'common:skills.bestPractices.description',
            fallback:
                'Write a clear description that says WHAT the skill does and WHEN to use it. The assistant matches your request against this to decide when to use the skill, so specific words help.',
        },
        {
            key: 'common:skills.bestPractices.focused',
            fallback:
                'Keep instructions short and focused on one job. Use numbered steps in the order they should happen.',
        },
        {
            key: 'common:skills.bestPractices.examples',
            fallback: 'Add a short example of a request the skill handles and the ideal result.',
        },
        {
            key: 'common:skills.bestPractices.keywords',
            fallback:
                'Add a few keywords (brand names, aliases, file types) to help the assistant find the skill from everyday wording.',
        },
        {
            key: 'common:skills.bestPractices.activation',
            fallback:
                'Choose “Automatically” for skills the assistant should reach for on its own, or “Only when I choose it” for ones you want to trigger deliberately with / or @.',
        },
        {
            key: 'common:skills.bestPractices.trust',
            fallback:
                'Only import skills from people you trust — a skill can tell the assistant to run tasks on your computer.',
        },
    ];

    return (
        <Panel>
            <Header type="button" aria-expanded={open} onClick={() => setOpen((prev) => !prev)}>
                <Icons name="idea" size={15} />
                <LabelMedium>{t('common:skills.bestPractices.title', 'Tips for great skills')}</LabelMedium>
                <Chevron>
                    <Icons name={open ? 'collapseArrow' : 'expandArrow'} size={12} />
                </Chevron>
            </Header>
            {open && (
                <TipList>
                    {tips.map((tip) => (
                        <Tip key={tip.key}>
                            <Dot aria-hidden />
                            <BodySmall>{t(tip.key, tip.fallback)}</BodySmall>
                        </Tip>
                    ))}
                </TipList>
            )}
        </Panel>
    );
}

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    border-radius: 0.5rem;
    border: 1px solid ${(props) => props.theme.color.border};
    background: ${(props) => props.theme.color.accentCard};
`;

const Header = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: ${(props) => props.theme.color.accent};
    text-align: left;

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.color.accent};
        outline-offset: 2px;
        border-radius: 0.25rem;
    }
`;

const Chevron = styled.span`
    margin-left: auto;
    display: inline-flex;
    color: ${(props) => props.theme.color.accent};
`;

const TipList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Tip = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
`;

const Dot = styled.span`
    flex: none;
    width: 0.4rem;
    height: 0.4rem;
    margin-top: 0.45rem;
    border-radius: 50%;
    background: ${(props) => props.theme.color.accent};
`;
